import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiChevronDown, FiHeart, FiMenu, FiSearch, FiShoppingBag, FiX } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/header.scss";

function Header() {
  const { cartCount, wishlist } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const menuTriggerRef = useRef(null);
  const searchTriggerRef = useRef(null);
  const menuPanelRef = useRef(null);
  const searchPanelRef = useRef(null);
  const searchInputRef = useRef(null);
  const moreRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
    setMoreOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const closeMore = (event) => {
      if (event.key === "Escape" || (event.type === "pointerdown" && !moreRef.current?.contains(event.target))) {
        setMoreOpen(false);
      }
    };
    document.addEventListener("keydown", closeMore);
    document.addEventListener("pointerdown", closeMore);
    return () => {
      document.removeEventListener("keydown", closeMore);
      document.removeEventListener("pointerdown", closeMore);
    };
  }, [moreOpen]);

  useEffect(() => {
    if (!menuOpen && !searchOpen) return undefined;

    const panel = menuOpen ? menuPanelRef.current : searchPanelRef.current;
    const trigger = menuOpen ? menuTriggerRef.current : searchTriggerRef.current;
    const firstTarget = menuOpen
      ? panel?.querySelector(".mobile-navigation__close")
      : searchInputRef.current;
    const focusTimer = window.setTimeout(() => firstTarget?.focus(), 0);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setSearchOpen(false);
        return;
      }

      if (event.key !== "Tab" || !panel) return;
      const focusable = [...panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.body.classList.add("nav-open");
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.classList.remove("nav-open");
      document.removeEventListener("keydown", handleKeyDown);
      trigger?.focus();
    };
  }, [menuOpen, searchOpen]);

  const searchResults = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return [];
    return productData
      .filter((product) =>
        [product.name, ...product.colors, product.category, product.gender]
          .join(" ")
          .toLowerCase()
          .includes(query)
      )
      .slice(0, 4);
  }, [searchValue]);

  const submitSearch = (event) => {
    event.preventDefault();
    const query = searchValue.trim();
    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : "/shop");
  };

  const navItems = [
    { label: "New in", to: "/shop?status=new-in" },
    { label: "Women", to: "/shop?collection=women" },
    { label: "Men", to: "/shop?collection=men" },
    { label: "Essentials", to: "/shop?category=Knitwear" },
    { label: "Our story", to: "/about" },
  ];
  const moreItems = [
    ["Privacy", "/privacy"],
    ["Terms", "/terms"],
    ["Shipping", "/shipping"],
    ["Returns", "/returns"],
    ["FAQ", "/faq"],
    ["Cookie notice", "/cookies"],
    ["All policies", "/policies"],
  ];

  return (
    <header className="site-header">
      <Link className="announcement-bar" to="/shipping">
        <span>{siteConfig.business.region} storefront</span>
        <strong>{siteConfig.business.currency} checkout · fixed {formatPrice(siteConfig.commerce.standardShipping)} shipping · {siteConfig.commerce.paymentPlatform} payment</strong>
        <span aria-hidden="true">→</span>
      </Link>

      <div className="site-header__nav">
        <button
          ref={menuTriggerRef}
          className="header-icon header-menu-button"
          type="button"
          onClick={() => {
            setSearchOpen(false);
            setMenuOpen(true);
          }}
          aria-label="Open menu"
          aria-expanded={menuOpen}
          aria-controls="mobile-navigation"
        >
          <FiMenu />
        </button>

        <nav className="desktop-navigation" aria-label="Primary navigation">
          {navItems.map((item) => (
            <Link key={item.label} to={item.to}>
              {item.label}
            </Link>
          ))}
          <div className="more-navigation" ref={moreRef}>
            <button type="button" onClick={() => setMoreOpen((open) => !open)} aria-expanded={moreOpen} aria-controls="more-navigation-menu">
              More <FiChevronDown aria-hidden="true" />
            </button>
            {moreOpen && <div id="more-navigation-menu" className="more-navigation__menu">
              {moreItems.map(([label, to]) => <Link key={to} to={to}>{label}<span aria-hidden="true">↗</span></Link>)}
            </div>}
          </div>
        </nav>

        <Link className="site-wordmark" to="/" aria-label={`${siteConfig.brandName} home`}>
          <span aria-hidden="true" className="site-wordmark__symbol"><i />{siteConfig.logo.symbol}</span>
          <span className="site-wordmark__letters"><b>{siteConfig.logo.wordmark.slice(0, 3)}</b><em>{siteConfig.logo.wordmark.slice(3)}</em></span>
        </Link>

        <div className="header-actions">
          <button
            ref={searchTriggerRef}
            className="header-icon header-search-button"
            type="button"
            onClick={() => {
              setMenuOpen(false);
              setSearchOpen(true);
            }}
            aria-label="Search"
            aria-expanded={searchOpen}
            aria-controls="site-search"
          >
            <FiSearch />
            <span className="header-action-label">Search</span>
          </button>
          <Link className="header-icon" to="/shop?wishlist=true" aria-label={`Wishlist, ${wishlist.length} items`}>
            <FiHeart />
            {wishlist.length > 0 && <span className="header-count">{wishlist.length}</span>}
          </Link>
          <Link className="header-icon" to="/cartpage" aria-label={`Shopping bag, ${cartCount} items`}>
            <FiShoppingBag />
            <span className="header-action-label">Bag</span>
            <span className="header-count header-count--bag">{cartCount}</span>
          </Link>
        </div>
      </div>

      {menuOpen && <div id="mobile-navigation" className="mobile-navigation is-open" role="dialog" aria-modal="true" aria-label="Site navigation" ref={menuPanelRef}>
        <button type="button" className="mobile-navigation__close" onClick={() => setMenuOpen(false)} aria-label="Close menu">
          <FiX />
        </button>
        <p className="eyebrow">Explore</p>
        <nav aria-label="Mobile navigation">
          {navItems.map((item) => (
            <Link key={item.label} to={item.to}>
              {item.label}
              <span aria-hidden="true">↗</span>
            </Link>
          ))}
          {moreItems.map(([label, to]) => (
            <Link key={to} to={to}>{label}<span aria-hidden="true">↗</span></Link>
          ))}
        </nav>
        <div className="mobile-navigation__footer">
          <Link to="/contact">Customer care</Link>
          <span>{siteConfig.tagline}</span>
        </div>
      </div>}

      {searchOpen && <div id="site-search" className="search-panel is-open" role="dialog" aria-modal="true" aria-label="Product search" ref={searchPanelRef}>
        <div className="search-panel__top">
          <span className="eyebrow">Find the right piece</span>
          <button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search">
            <FiX />
          </button>
        </div>
        <form className="search-panel__form" onSubmit={submitSearch}>
          <FiSearch aria-hidden="true" />
          <input
            ref={searchInputRef}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search by style, colour or category"
            aria-label="Search products"
          />
          <button type="submit">Search</button>
        </form>
        {searchValue && (
          <div className="search-panel__results" aria-live="polite">
            {searchResults.length ? (
              searchResults.map((product) => (
                <Link key={product.id} to={`/shopDetails/${product.id}`}>
                  <img src={product.image} alt="" />
                  <span>
                    <strong>{product.name}</strong>
                    <small>{product.colorName}</small>
                  </span>
                </Link>
              ))
            ) : (
              <p>No pieces found. Try another term.</p>
            )}
          </div>
        )}
      </div>}

      {(menuOpen || searchOpen) && (
        <button
          className="header-backdrop"
          type="button"
          onClick={() => {
            setMenuOpen(false);
            setSearchOpen(false);
          }}
          aria-label="Close navigation"
        />
      )}
    </header>
  );
}

export default Header;
